import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/PageLayout';
import { Phone, MapPin, Building2, CheckCircle2 } from 'lucide-react';

const TaliparambaLanding = () => {
  return (
    <>
      <Helmet>
        <title>Villa Builders in Taliparamba | Custom Home Construction</title>
        <meta name="description" content="Master Home is a premium villa builder and home construction expert serving Taliparamba, Kerala. Expert turnkey construction and custom house designs." />
        <link rel="canonical" href="https://masterhome.co.in/locations/taliparamba" />
      </Helmet>

      <PageLayout
        title="Luxury Villa Builders in Taliparamba, Kerala"
        subtitle="Uncompromising quality and custom designs for the modern Kerala family."
      >
        <div className="space-y-16">
          {/* Introduction */}
          <section className="glass-card p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6 text-gradient">Your Expert Home Construction Partner</h2>
            <div className="prose max-w-none text-gray-600 text-lg leading-relaxed">
              <p className="mb-4">
                Taliparamba is witnessing rapid residential growth, with families expanding and NRI investments driving demand for modern, spacious homes. If you're building a new house in the Taliparamba area, you need a builder who understands the balance between traditional Kerala aesthetics and contemporary luxury. Finding contractors who won't inflate quotes or compromise on cement and steel quality is the biggest challenge homeowners face.
              </p>
              <p>
                At Master Home, we offer completely transparent turnkey villa construction. From initial architectural blueprints that maximize your plot's potential to the final coat of weather-proof emulsion, we are the foremost custom home builders in Taliparamba.
              </p>
            </div>
          </section>

          {/* Local Proof */}
          <section className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6 lg:pr-8">
              <h2 className="text-3xl font-bold text-gray-900">Built for Kerala's Seasons</h2>
              <p className="text-gray-600 text-lg">
                Building near the coastal slopes or inland towards the lush laterite grounds of Taliparamba requires specific material considerations. We ensure heavy-duty waterproofing against the monsoons and high ceilings for natural cooling during the summer months.
              </p>
              <p className="text-gray-600 text-lg">
                We've managed extensive construction projects near major local landmarks and residential pockets, bringing top-tier engineering standards to every neighborhood.
              </p>
            </div>
            <div className="bg-primary-50 p-8 rounded-2xl border border-primary-100">
              <h3 className="text-xl font-bold text-primary-800 mb-4 flex items-center gap-2">
                <MapPin className="text-primary-600" /> Taliparamba Service Areas
              </h3>
              <ul className="space-y-3">
                {['Dharmasala', 'Kuppam', 'Bakkalam', 'Parassinikadavu', 'Trichambaram'].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-primary-500" />
                    Residential Projects in {area}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Services */}
          <section className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 p-8">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Local Contracting Services</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold">Luxury Villas</h3>
                <p className="text-gray-600">Beautiful multi-story villas featuring modern elevations, private car porches, and Vastu-compliant layouts.</p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold">Turnkey Packages</h3>
                <p className="text-gray-600">Stress-free building. We manage labor, heavy machinery, masonry, plumbing, and electrical installations end-to-end.</p>
              </div>
              <div className="space-y-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                  <Building2 size={24} />
                </div>
                <h3 className="text-xl font-bold">Home Extensions</h3>
                <p className="text-gray-600">Adding extra bedrooms, balconies, or remodeling outdated facades for existing Taliparamba properties.</p>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className="text-center max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">Why Choose Master Home?</h2>
            <p className="text-xl text-gray-600">
              Our 10+ years of local experience means we know the best material suppliers in Taliparamba. We promise zero hidden costs, clear communication, and impeccable structural integrity. We treat your future home with the respect and diligence it deserves.
            </p>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-12 text-center text-white shadow-2xl">
            <h2 className="text-3xl font-bold mb-4">Launch Your Project Today</h2>
            <p className="text-xl mb-8 opacity-90">Book a site evaluation and get an honest, detailed construction estimate.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-4 text-primary-700 bg-white rounded-full font-bold text-lg hover:bg-gray-50 transition-colors shadow-lg">
                <Phone className="mr-2" /> Call +91 9567810944
              </a>
              <a href="https://wa.me/919567810944" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-white border-2 border-white rounded-full font-bold text-lg hover:bg-white/10 transition-colors">
                WhatsApp Connect
              </a>
            </div>
          </section>
        </div>
      </PageLayout>
    </>
  );
};

export default TaliparambaLanding;
